function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { each, filter, pipe } from "@fxts/core";
import elementFromHtml from "./lib/_elementFromHtml";
import _find from "./lib/_find";
import _toIterator from "./lib/_toIterator";
import { _nextOrPrevAll } from "./lib/_nextOrPrevAll";
import { _nextOrPrev } from "./lib/_nextOrPrev";
import _toCamel from "./lib/_toCamel";
import _toDash from "./lib/_toDash";
import { _entries } from "./lib/_entries";
export class $Element {
    throwIfNull(error) {
        return $Element.throwIfNull(this._element, error);
    }
    element() {
        return this._element;
    }
    end() {
        return this._prev$Element;
    }
    find(selector) {
        return $(_find("querySelector", selector, this._element), this, this._error);
    }
    findAll(selector) {
        return $Element.to$Elements(_find("querySelectorAll", selector, this._element));
    }
    closest(selector) {
        return $(this._element.closest(selector), this, this._error);
    }
    children() {
        return $Element.to$Elements(_toIterator(this._element.children));
    }
    prevAll(selector) {
        return $Element.to$Elements(_nextOrPrevAll("previousElementSibling", "unshift", selector, this._element));
    }
    nextAll(selector) {
        return $Element.to$Elements(_nextOrPrevAll("nextElementSibling", "push", selector, this._element));
    }
    prev(selector) {
        return $(_nextOrPrev("previousElementSibling", selector, this._element));
    }
    next(selector) {
        return $(_nextOrPrev("nextElementSibling", selector, this._element));
    }
    siblings(selector) {
        return [
            ..._toIterator(this._element.parentNode.children)
        ].filter((element2)=>this._element !== element2 && element2.matches(selector));
    }
    parentNode() {
        return $(this._element.parentNode, this, this._error);
    }
    is(selector) {
        return this._element.matches(selector);
    }
    matches(selector) {
        return this.is(selector);
    }
    contains(child) {
        return this._element.contains($(child)._element);
    }
    getValue() {
        const value = this._element.value;
        if (value === undefined) {
            throw new Error("This element has no property defined as 'value'.");
        }
        return value;
    }
    setValue(value) {
        this._element.value = value;
        return this;
    }
    floatValue() {
        return parseFloat(this.getValue());
    }
    getAttribute(name) {
        return this._element.getAttribute(name);
    }
    getAttributes(names) {
        return Object.fromEntries(names.map((name)=>[
                _toCamel(name),
                this._element.getAttribute(name)
            ]));
    }
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */ setAttribute(name, value) {
        this._element.setAttribute(_toDash(name), value);
        return this;
    }
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */ setAttributes(attributes) {
        Object.entries(attributes).forEach((param)=>{
            let [name, value] = param;
            return $(this).setAttribute(name, value);
        });
        return this;
    }
    removeAttribute(name) {
        this._element.removeAttribute(name);
        return this;
    }
    getInnerHtml() {
        return this._element.innerHTML;
    }
    setInnerHtml(html) {
        this._element.innerHTML = html;
        return this;
    }
    getTextContent() {
        return this._element.textContent;
    }
    setTextContent(html) {
        this._element.textContent = html;
        return this;
    }
    addClass() {
        for(var _len = arguments.length, classNames = new Array(_len), _key = 0; _key < _len; _key++){
            classNames[_key] = arguments[_key];
        }
        this._element.classList.add(...classNames);
        return this;
    }
    removeClass() {
        for(var _len = arguments.length, classNames = new Array(_len), _key = 0; _key < _len; _key++){
            classNames[_key] = arguments[_key];
        }
        this._element.classList.remove(...classNames);
        return this;
    }
    hasClass(className) {
        return this._element.classList.contains(className);
    }
    getComputedStyle(property) {
        var _element_ownerDocument_defaultView;
        const element = this._element;
        this._element.style;
        return (_element_ownerDocument_defaultView = element.ownerDocument.defaultView) === null || _element_ownerDocument_defaultView === void 0 ? void 0 : _element_ownerDocument_defaultView.getComputedStyle(element, null)[_toCamel(property.toString())];
    }
    getComputedStyles(properties) {
        return Object.fromEntries(properties.map((property)=>[
                _toCamel(property.toString()),
                this.getComputedStyle(property)
            ]));
    }
    offsetFromBody() {
        const rect = this._element.getBoundingClientRect();
        return {
            top: rect.top + window.scrollY,
            left: rect.left + window.scrollX
        };
    }
    append() {
        for(var _len = arguments.length, children = new Array(_len), _key = 0; _key < _len; _key++){
            children[_key] = arguments[_key];
        }
        this._element.append(...$Element.toHTMLElements(children));
        return this;
    }
    appendTo(parent) {
        $(parent).append(this._element);
        return this;
    }
    prepend() {
        for(var _len = arguments.length, children = new Array(_len), _key = 0; _key < _len; _key++){
            children[_key] = arguments[_key];
        }
        this._element.prepend(...$Element.toHTMLElements(children));
        return this;
    }
    prependTo(parent) {
        $(parent).prepend(this._element);
        return this;
    }
    after() {
        for(var _len = arguments.length, children = new Array(_len), _key = 0; _key < _len; _key++){
            children[_key] = arguments[_key];
        }
        this._element.after(...$Element.toHTMLElements(children));
        return this;
    }
    before() {
        for(var _len = arguments.length, children = new Array(_len), _key = 0; _key < _len; _key++){
            children[_key] = arguments[_key];
        }
        this._element.before(...$Element.toHTMLElements(children));
        return this;
    }
    remove() {
        this._element.remove();
        return this;
    }
    delegate(eventType, selector, listener) {
        this._element.addEventListener(eventType, (e)=>{
            pipe(this.findAll(selector), filter(($currentTarget)=>{
                if (!e.target) return false;
                return $currentTarget.contains(e.target);
            }), each(($currentTarget)=>{
                listener.call(this._element, {
                    ...Object.fromEntries(_entries(e)),
                    originalEvent: e,
                    target: e.target,
                    currentTarget: $currentTarget._element
                });
            }));
        });
        return this;
    }
    /* eslint-enable @typescript-eslint/no-explicit-any */ to(f) {
        return f(this._element);
    }
    chain(f) {
        const element2 = f(this._element);
        return !element2 || element2 === this._element ? this : $(element2);
    }
    static toHTMLElements($elements) {
        return $elements.map((child)=>$(child)._element);
    }
    constructor(element, prev$Element, error){
        _define_property(this, "_element", void 0);
        _define_property(this, "_prev$Element", void 0);
        _define_property(this, "_error", void 0);
        if (element === undefined) throw new Error("element is undefined");
        this._element = element;
        this._prev$Element = prev$Element;
        this._error = error;
    }
}
_define_property($Element, "all", (selector)=>{
    return $Element.to$Elements(document.querySelectorAll(selector));
});
_define_property($Element, "fromHtml", (htmlStr)=>{
    return $(elementFromHtml(htmlStr));
});
_define_property($Element, "to$Elements", (elements)=>{
    return [
        ..._toIterator(elements)
    ].map((element)=>$(element));
});
_define_property($Element, "throwIfNull", (element, throwIfNullError)=>{
    return $(element, undefined, throwIfNullError || new Error("HTMLElement not found."));
});
function $(element, prev$Element, throwIfNullError) {
    if (typeof element === "string") {
        return $(document.querySelector(element), prev$Element, throwIfNullError);
    }
    if (throwIfNullError && element === null) {
        throw throwIfNullError;
    }
    return element === null ? null : element instanceof $Element ? element : new $Element(element, prev$Element, throwIfNullError);
}
$.all = $Element.all;
$.fromHtml = $Element.fromHtml;
$.to$Elements = $Element.to$Elements;
$.throwIfNull = $Element.throwIfNull;
export { $ };
